import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardTile from './DashboardTile';
import type { RecentCourse } from '@/types/dashboard';

interface CoursesInProgressTileProps {
  courses: RecentCourse[];
}

const ProgressRing: FC<{ percent: number }> = ({ percent }) => (
  <div
    className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-2xs font-bold"
    style={{
      background: `conic-gradient(var(--tile-accent) ${percent * 3.6}deg, var(--tile-bar-bg) 0deg)`,
      color: 'var(--tile-accent)',
    }}
  >
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center text-2xs"
      style={{ background: 'var(--tile-bg)' }}
    >
      {percent}%
    </div>
  </div>
);

const CoursesInProgressTile: FC<CoursesInProgressTileProps> = ({ courses }) => {
  const navigate = useNavigate();

  if (courses.length === 0) {
    return (
      <DashboardTile wide onClick={() => navigate('/trilhas')} data-testid="courses-progress-tile">
        <p className="text-2xs font-bold uppercase tracking-[0.1em] mb-3" style={{ color: 'var(--tile-text-muted)' }}>
          Cursos em Andamento
        </p>
        <div className="flex flex-col items-center justify-center py-6 gap-2">
          <span className="material-symbols-outlined text-3xl" style={{ color: 'var(--tile-text-muted)' }}>
            menu_book
          </span>
          <p className="text-sm" style={{ color: 'var(--tile-text-muted)' }}>Comece uma trilha!</p>
          <span
            className="text-xs font-bold px-4 py-1.5 rounded-lg mt-1"
            style={{ background: 'var(--tile-accent)', color: 'var(--tile-bg)' }}
          >
            Explorar trilhas
          </span>
        </div>
      </DashboardTile>
    );
  }

  return (
    <DashboardTile wide data-testid="courses-progress-tile">
      <p className="text-2xs font-bold uppercase tracking-[0.1em] mb-3" style={{ color: 'var(--tile-text-muted)' }}>
        Cursos em Andamento
      </p>
      <div className="space-y-2.5">
        {courses.slice(0, 4).map((course) => (
          <div
            key={course.id}
            onClick={() => {
              if (course.lastLessonId) {
                navigate(`/trilhas/${course.id}/aula/${course.lastLessonId}`);
              } else {
                navigate(`/trilhas/${course.id}`);
              }
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(course.lastLessonId
                  ? `/trilhas/${course.id}/aula/${course.lastLessonId}`
                  : `/trilhas/${course.id}`
                );
              }
            }}
            className="flex items-center gap-3 cursor-pointer rounded-lg p-2 -mx-2 transition-colors"
            style={{ background: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--tile-bar-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <ProgressRing percent={course.progressPercent} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color: 'var(--tile-text-primary)' }}>
                {course.title}
              </p>
              <p className="text-2xs" style={{ color: 'var(--tile-text-muted)' }}>
                {course.xpEarned}/{course.xpTotal} XP
              </p>
            </div>
            <span className="material-symbols-outlined text-sm" style={{ color: 'var(--tile-text-muted)' }}>
              chevron_right
            </span>
          </div>
        ))}
      </div>
    </DashboardTile>
  );
};

export default CoursesInProgressTile;
